/**
 * spiritualPresenceEngine.js
 *
 * Resolves what type of spiritual presence Kevia should embody.
 *
 * DISTINCT from wisdom tone (how the response sounds) and mood (avatar animation).
 * Presence = WHO Kevia is in this pastoral moment.
 *
 * Types:
 *   contemplativa     — quiet, thoughtful, holding space for mystery
 *   consoladora       — soft presence with grief or loss
 *   firme             — steady, clear, grounded in urgency or conflict
 *   reverente         — appropriate to prayer, sermon, sacred themes
 *   acompañante       — default: walking alongside without imposing
 *   exhortativa_suave — gently inviting growth or action
 *   esperanzadora     — holding hope in front of discouragement
 *   silenciosa        — when fewer words are the truest ministry
 */

/**
 * @param {{
 *   mood:       string,
 *   emotion:    object|null,
 *   intent:     string,
 *   wisdomTone: string,
 *   riskLevel:  string,
 * }} params
 * @returns {{ presence: string, instruction: string|null }}
 */
export function resolveSpiritualPresence({ mood, emotion, intent, wisdomTone, riskLevel }) {
  // Acute grief → silenciosa (fewer words are the truest presence)
  if (
    emotion?.dominantEmotion === "grief" && emotion?.intensity === "high" ||
    (mood === "grief" && emotion?.intensity === "high")
  ) {
    return {
      presence: "silenciosa",
      instruction: "Sé presencia silenciosa. No llenes el espacio con palabras.",
    };
  }

  // Grief (moderate) → consoladora
  if (mood === "grief" || emotion?.dominantEmotion === "grief") {
    return { presence: "consoladora", instruction: null };
  }

  // Discouragement, fear, loneliness, exhaustion → esperanzadora
  if (["discouragement", "fear", "loneliness", "exhaustion"].includes(emotion?.dominantEmotion)) {
    return {
      presence: "esperanzadora",
      instruction: "Transmite esperanza tranquila, no optimismo artificial.",
    };
  }

  // Spiritual confusion or deep reflection → contemplativa
  if (emotion?.dominantEmotion === "spiritual_confusion" || intent === "reflection") {
    return {
      presence: "contemplativa",
      instruction: "Habla desde serenidad contemplativa. No resuelvas la tensión espiritual demasiado rápido.",
    };
  }

  // Sermon / prayer → reverente
  if (intent === "sermon" || intent === "prayer") {
    return { presence: "reverente", instruction: null };
  }

  // Urgency or active conflict → firme
  if (
    mood === "urgent" ||
    emotion?.dominantEmotion === "urgency" ||
    emotion?.dominantEmotion === "conflict"
  ) {
    return { presence: "firme", instruction: null };
  }

  // Celebration → esperanzadora with warmth
  if (mood === "celebration") {
    return { presence: "esperanzadora", instruction: null };
  }

  // Pastoral followup — gently guiding → exhortativa_suave
  if (
    (intent === "family_followup" || intent === "visit_plan") &&
    !emotion?.detected &&
    riskLevel === "none"
  ) {
    return { presence: "exhortativa_suave", instruction: null };
  }

  return { presence: "acompañante", instruction: null };
}
